import { io, Socket } from 'socket.io-client';
// @ts-ignore -- MessagePack parser doesn't have TypeScript definitions
import msgpackParser from 'socket.io-msgpack-parser';
import { quantizeMembranePoints, quantizePosition, quantizeRotation } from '../shared/NetworkPayload';
import { 
  PlayerState, 
  ResourceState, 
  RoomStateEvent,
  PlayerJoinedEvent,
  PlayerMovedEvent,
  ResourceCollectedEvent,
  ToxinShotEvent,
  CellDamagedEvent,
  OrganelleBuiltEvent,
  RoomsListEvent,
  RoomCreatedEvent,
  RoomInfo,
  PlayerRespawnedEvent,
  MembraneExpandedEvent,
  ResourcesUpdatedEvent,
  TickUpdateEvent,
  MembraneKeyframeEvent,
  FullSnapshotEvent,
  ServerModeEvent
} from '../types/multiplayer';

// Configuration constants
const DEFAULT_SERVER = {
  PROD_URL: 'https://cytopod.com',  // Change to your actual domain when you have one
  TEMP_PROD_URL: 'https://168.119.110.238',  // Temporary SSL with IP
  DEV_URL: 'http://localhost:3007'
};

// Type for callbacks to handle different events
type EventCallback<T> = (data: T) => void;

export class NetworkService {
  private socket: Socket | null = null;
  private connected: boolean = false;
  private static instance: NetworkService;
  private commandSeq: number = 0;
  private lastReceivedTick: number = 0;
  private quantizedPayloads: boolean = false;
  
  // Event callbacks
  private onConnectCallbacks: (() => void)[] = [];
  private onDisconnectCallbacks: (() => void)[] = [];
  private onRoomStateCallbacks: EventCallback<RoomStateEvent>[] = [];
  private onFullSnapshotCallbacks: EventCallback<FullSnapshotEvent>[] = [];
  private onServerModeCallbacks: EventCallback<ServerModeEvent>[] = [];
  private onPlayerJoinedCallbacks: EventCallback<PlayerJoinedEvent>[] = [];
  private onPlayerLeftCallbacks: EventCallback<string>[] = [];
  private onPlayerMovedCallbacks: EventCallback<PlayerMovedEvent>[] = [];
  private onResourceSpawnedCallbacks: EventCallback<ResourceState>[] = [];
  private onResourceCollectedCallbacks: EventCallback<ResourceCollectedEvent>[] = [];
  private onToxinShotCallbacks: EventCallback<ToxinShotEvent>[] = [];
  private onCellDamagedCallbacks: EventCallback<CellDamagedEvent>[] = [];
  private onOrganelleBuiltCallbacks: EventCallback<OrganelleBuiltEvent>[] = [];
  private onRoomsListCallbacks: EventCallback<RoomInfo[]>[] = [];
  private onRoomCreatedCallbacks: EventCallback<RoomCreatedEvent>[] = [];
  private onErrorCallbacks: EventCallback<{message: string}>[] = [];
  private onPlayerDiedCallbacks: EventCallback<string>[] = [];
  private onPlayerRespawnedCallbacks: EventCallback<PlayerRespawnedEvent>[] = [];
  private onMembraneExpandedCallbacks: EventCallback<MembraneExpandedEvent>[] = [];
  private onMembraneExpansionFailedCallbacks: (() => void)[] = [];
  private onMembraneKeyframeCallbacks: EventCallback<MembraneKeyframeEvent>[] = [];
  private onResourcesUpdatedCallbacks: EventCallback<ResourcesUpdatedEvent>[] = [];
  private onTickUpdateCallbacks: EventCallback<TickUpdateEvent>[] = [];
  
  private constructor() {
    if (typeof window !== 'undefined') {
      this.quantizedPayloads = window.localStorage.getItem('cytopod_quantized_payloads') === 'true';
    }
    // No longer connecting in constructor
    // Connection will be established when connect() is called
  }
  
  /**
   * Get the singleton instance
   */
  public static getInstance(): NetworkService {
    if (!NetworkService.instance) {
      NetworkService.instance = new NetworkService();
    }
    return NetworkService.instance;
  }
  
  /**
   * Connect to the server
   * Only called when we actually need to connect
   */
  public connect(): void {
    if (this.socket) {
      console.log('Socket connection already exists');
      return;
    }
    
    // Environment detection - browser environment doesn't have process.env
    const isBrowser = typeof window !== 'undefined';
    
    // For browser client, use window.location.origin or fallback to configured URL
    let serverUrl: string;
    
    if (isBrowser) {
      // In the browser, determine if we're in production by the hostname
      const isLocalhost = window.location.hostname === 'localhost' || 
                         window.location.hostname === '127.0.0.1';
                         
      if (!isLocalhost) {
        // In production, use the same origin or the production server
        serverUrl = window.location.origin || DEFAULT_SERVER.PROD_URL;
      } else {
        // In development, use localhost
        serverUrl = DEFAULT_SERVER.DEV_URL;
      }
    } else {
      // In Node.js environment (server-side), use environment variables
      const isProduction = process.env.NODE_ENV === 'production';
      serverUrl = isProduction 
        ? process.env.SERVER_URL || DEFAULT_SERVER.PROD_URL
        : process.env.DEV_SERVER_URL || DEFAULT_SERVER.DEV_URL;
    }
    
    console.log(`Connecting to server at: ${serverUrl}`);
    this.socket = io(serverUrl, {
      parser: msgpackParser // Use MessagePack for more efficient binary encoding
    });
    
    this.setupSocketListeners();
  }
  
  /**
   * Set up all socket event listeners
   */
  private setupSocketListeners(): void {
    if (!this.socket) {
      console.error('Cannot setup listeners: socket is null');
      return;
    }
    
    // Connection events
    this.socket.on('connect', () => {
      console.log('Connected to game server');
      this.connected = true;
      this.onConnectCallbacks.forEach(callback => callback());
    });
    
    this.socket.on('disconnect', () => {
      console.log('Disconnected from game server');
      this.connected = false;
      this.onDisconnectCallbacks.forEach(callback => callback());
    });
    
    // Game state events
    this.socket.on('roomState', (data: RoomStateEvent) => {
      console.log('🔄 RECEIVED ROOM STATE EVENT:', {
        playerCount: data.players.length,
        playerIds: data.players.map(p => p.id),
        resourceCount: data.resources.length,
        myId: this.socket?.id,
        timestamp: new Date().toISOString()
      });
      this.onRoomStateCallbacks.forEach(callback => callback(data));
    });

    this.socket.on('fullSnapshot', (data: FullSnapshotEvent) => {
      this.lastReceivedTick = Math.max(this.lastReceivedTick, data.tick);
      this.onFullSnapshotCallbacks.forEach(callback => callback(data));
    });

    this.socket.on('serverMode', (data: ServerModeEvent) => {
      this.onServerModeCallbacks.forEach(callback => callback(data));
    });
    
    this.socket.on('playerJoined', (data: PlayerJoinedEvent) => {
      this.onPlayerJoinedCallbacks.forEach(callback => callback(data));
    });
    
    this.socket.on('playerLeft', (playerId: string) => {
      this.onPlayerLeftCallbacks.forEach(callback => callback(playerId));
    });
    
    this.socket.on('playerMoved', (data: PlayerMovedEvent) => {
      this.onPlayerMovedCallbacks.forEach(callback => callback(data));
    });
    
    this.socket.on('resourceSpawned', (data: ResourceState) => {
      this.onResourceSpawnedCallbacks.forEach(callback => callback(data));
    });
    
    this.socket.on('resourceCollected', (data: ResourceCollectedEvent) => {
      this.onResourceCollectedCallbacks.forEach(callback => callback(data));
    });
    
    this.socket.on('toxinShot', (data: ToxinShotEvent) => {
      this.onToxinShotCallbacks.forEach(callback => callback(data));
    });
    
    this.socket.on('cellDamaged', (data: CellDamagedEvent) => {
      this.onCellDamagedCallbacks.forEach(callback => callback(data));
    });
    
    this.socket.on('playerDied', (playerId: string) => {
      this.onPlayerDiedCallbacks.forEach(callback => callback(playerId));
    });
    
    this.socket.on('playerRespawned', (data: PlayerRespawnedEvent) => {
      this.onPlayerRespawnedCallbacks.forEach(callback => callback(data));
    });
    
    this.socket.on('organelleBuilt', (data: OrganelleBuiltEvent) => {
      this.onOrganelleBuiltCallbacks.forEach(callback => callback(data));
    });
    
    this.socket.on('roomsList', (data: RoomsListEvent) => {
      console.log('Received roomsList event from server:', data);
      if (!data || !data.rooms) {
        console.warn('Invalid roomsList data received:', data);
        // Ensure we always pass an array to callbacks
        this.onRoomsListCallbacks.forEach(callback => callback([]));
      } else {
        // Pass the rooms array to callbacks
        this.onRoomsListCallbacks.forEach(callback => callback(data.rooms));
      }
    });
    
    this.socket.on('roomCreated', (data: RoomCreatedEvent) => {
      console.log('Received roomCreated event from server:', data);
      if (!data || !data.id) {
        console.warn('Invalid roomCreated data received:', data);
      }
      this.onRoomCreatedCallbacks.forEach(callback => callback(data));
    });
    
    this.socket.on('error', (data: {message: string}) => {
      console.error('Server error:', data.message);
      this.onErrorCallbacks.forEach(callback => callback(data));
    });
    
    this.socket.on('membraneExpanded', (data: MembraneExpandedEvent) => {
      this.onMembraneExpandedCallbacks.forEach(callback => callback(data));
    });
    
    this.socket.on('membraneExpansionFailed', () => {
      this.onMembraneExpansionFailedCallbacks.forEach(callback => callback());
    });

    this.socket.on('membraneKeyframe', (data: MembraneKeyframeEvent) => {
      this.onMembraneKeyframeCallbacks.forEach(callback => callback(data));
    });

    // Resources updated (ATP consumption from movement)
    this.socket.on('resourcesUpdated', (data: ResourcesUpdatedEvent) => {
      this.onResourcesUpdatedCallbacks.forEach(callback => callback(data));
    });

    this.socket.on('tickUpdate', (data: TickUpdateEvent) => {
      this.lastReceivedTick = Math.max(this.lastReceivedTick, data.tick);
      this.onTickUpdateCallbacks.forEach(callback => callback(data));
    });
  }
  
  /**
   * Get the socket ID
   */
  public getSocketId(): string {
    return this.socket?.id || '';
  }
  
  /**
   * Get the socket instance (for NetworkMonitor use)
   */
  public getSocket(): Socket | null {
    return this.socket;
  }
  
  /**
   * Check if connected to server
   */
  public isConnected(): boolean {
    return this.connected;
  }
  
  // Socket emit methods
  
  /**
   * Join a game room
   */
  public joinRoom(roomId: string, playerName: string = 'Player'): void {
    if (!this.ensureConnected()) return;
    
    console.log(`🎮 JOINING ROOM: ${roomId} as ${playerName}`);
    
    // Send join room request to server
    this.socket!.emit('joinRoom', { roomId, playerName });
  }
  
  /**
   * Send position update
   */
  public updatePosition(x: number, y: number, rotation: number, points?: {x: number, y: number, angle: number}[]): void {
    if (!this.ensureConnected()) return;
    this.commandSeq += 1;
    const payload = this.quantizedPayloads
      ? {
          x: quantizePosition(x),
          y: quantizePosition(y),
          rotation: quantizeRotation(rotation),
          points: points ? quantizeMembranePoints(points) : undefined
        }
      : { x, y, rotation, points };
    this.socket!.emit('updatePosition', {
      ...payload,
      clientSeq: this.commandSeq,
      lastAckTick: this.lastReceivedTick
    });
  }

  public updateMembrane(points: {x: number, y: number, angle: number}[]): void {
    if (!this.ensureConnected()) return;
    this.socket!.emit('updateMembrane', {
      points: this.quantizedPayloads ? quantizeMembranePoints(points) : points
    });
  }

  public requestSnapshot(): void {
    if (!this.ensureConnected()) return;
    this.socket!.emit('requestSnapshot');
  }

  public setQuantizedPayloads(enabled: boolean): void {
    this.quantizedPayloads = enabled;
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('cytopod_quantized_payloads', enabled ? 'true' : 'false');
    }
  }
  
  /**
   * Collect a resource
   */
  public collectResource(resourceId: string): void {
    if (!this.ensureConnected()) return;
    this.socket!.emit('collectResource', { id: resourceId });
  }
  
  /**
   * Shoot a toxin
   */
  public shootToxin(startX: number, startY: number, angle: number): void {
    if (!this.ensureConnected()) return;
    this.socket!.emit('shootToxin', { startX, startY, angle });
  }
  
  /**
   * Hit a player with toxin
   */
  public hitPlayer(playerId: string, angle: number, amount: number = 10): void {
    if (!this.ensureConnected()) return;
    this.socket!.emit('hitPlayer', { playerId, angle, amount });
  }
  
  /**
   * Build an organelle
   */
  public buildOrganelle(type: string): void {
    if (!this.ensureConnected()) return;
    this.socket!.emit('buildOrganelle', { type });
  }
  
  /**
   * Get list of available rooms
   */
  public getRooms(): void {
    if (!this.ensureConnected()) return;
    console.log('Sending getRooms event to server');
    this.socket!.emit('getRooms');
  }
  
  /**
   * Create a new room
   */
  public createRoom(name: string, maxPlayers: number = 8): void {
    if (!this.ensureConnected()) return;
    console.log(`Sending createRoom event to server: name=${name}, maxPlayers=${maxPlayers}`);
    this.socket!.emit('createRoom', { name, maxPlayers });
  }
  
  /**
   * Helper method to ensure connection exists
   * Returns true if connected, false otherwise
   */
  private ensureConnected(): boolean {
    if (!this.socket || !this.connected) {
      console.warn('Socket not connected. Cannot send event.');
      return false;
    }
    return true;
  }
  
  // Event registration methods
  
  // Connection events
  public onConnect(callback: () => void): void {
    this.onConnectCallbacks.push(callback);
    if (this.connected) {
      callback();
    }
  }
  
  /**
   * Remove a specific connect callback
   * @param callback The callback function to remove
   * @returns true if callback was found and removed, false otherwise
   */
  public offConnect(callback: () => void): boolean {
    const index = this.onConnectCallbacks.indexOf(callback);
    if (index !== -1) {
      this.onConnectCallbacks.splice(index, 1);
      return true;
    }
    return false;
  }
  
  public onDisconnect(callback: () => void): void {
    this.onDisconnectCallbacks.push(callback);
  }
  
  // Game state events
  public onRoomState(callback: EventCallback<RoomStateEvent>): void {
    this.onRoomStateCallbacks.push(callback);
  }

  public onFullSnapshot(callback: EventCallback<FullSnapshotEvent>): void {
    this.onFullSnapshotCallbacks.push(callback);
  }

  public onServerMode(callback: EventCallback<ServerModeEvent>): void {
    this.onServerModeCallbacks.push(callback);
  }
  
  public onPlayerJoined(callback: EventCallback<PlayerJoinedEvent>): void {
    this.onPlayerJoinedCallbacks.push(callback);
  }
  
  public onPlayerLeft(callback: EventCallback<string>): void {
    this.onPlayerLeftCallbacks.push(callback);
  }
  
  public onPlayerMoved(callback: EventCallback<PlayerMovedEvent>): void {
    this.onPlayerMovedCallbacks.push(callback);
  }
  
  public onResourceSpawned(callback: EventCallback<ResourceState>): void {
    this.onResourceSpawnedCallbacks.push(callback);
  }
  
  public onResourceCollected(callback: EventCallback<ResourceCollectedEvent>): void {
    this.onResourceCollectedCallbacks.push(callback);
  }
  
  public onToxinShot(callback: EventCallback<ToxinShotEvent>): void {
    this.onToxinShotCallbacks.push(callback);
  }
  
  public onCellDamaged(callback: EventCallback<CellDamagedEvent>): void {
    this.onCellDamagedCallbacks.push(callback);
  }
  
  public onOrganelleBuilt(callback: EventCallback<OrganelleBuiltEvent>): void {
    this.onOrganelleBuiltCallbacks.push(callback);
  }
  
  public onRoomsList(callback: EventCallback<RoomInfo[]>): void {
    this.onRoomsListCallbacks.push(callback);
  }
  
  /**
   * Remove a specific rooms list callback
   * @param callback The callback function to remove
   * @returns true if callback was found and removed, false otherwise
   */
  public offRoomsList(callback: EventCallback<RoomInfo[]>): boolean {
    const index = this.onRoomsListCallbacks.indexOf(callback);
    if (index !== -1) {
      this.onRoomsListCallbacks.splice(index, 1);
      return true;
    }
    return false;
  }
  
  public onRoomCreated(callback: EventCallback<RoomCreatedEvent>): void {
    this.onRoomCreatedCallbacks.push(callback);
  }
  
  public onError(callback: EventCallback<{message: string}>): void {
    this.onErrorCallbacks.push(callback);
  }
  
  /**
   * Register a callback for when a player dies
   */
  public onPlayerDied(callback: EventCallback<string>): void {
    this.onPlayerDiedCallbacks.push(callback);
  }
  
  /**
   * Register a callback for when a player respawns
   */
  public onPlayerRespawned(callback: EventCallback<PlayerRespawnedEvent>): void {
    this.onPlayerRespawnedCallbacks.push(callback);
  }

  /**
   * Register callback for membrane expansion success
   */
  public onMembraneExpanded(callback: EventCallback<MembraneExpandedEvent>): void {
    this.onMembraneExpandedCallbacks.push(callback);
  }
  
  /**
   * Register callback for membrane expansion failure
   */
  public onMembraneExpansionFailed(callback: () => void): void {
    this.onMembraneExpansionFailedCallbacks.push(callback);
  }
  
  /**
   * Request membrane expansion
   */
  public expandMembrane(): void {
    if (this.ensureConnected()) {
      this.socket?.emit('expandMembrane');
    }
  }
  
  /**
   * Disconnect from the server and clean up event listeners
   */
  public disconnect(): void {
    if (this.socket) {
      // Remove socket event listeners
      this.socket.off('connect');
      this.socket.off('disconnect');
      this.socket.off('roomState');
      this.socket.off('fullSnapshot');
      this.socket.off('serverMode');
      this.socket.off('playerJoined');
      this.socket.off('playerLeft');
      this.socket.off('playerMoved');
      this.socket.off('resourceSpawned');
      this.socket.off('resourceCollected');
      this.socket.off('toxinShot');
      this.socket.off('cellDamaged');
      this.socket.off('organelleBuilt');
      this.socket.off('roomsList');
      this.socket.off('roomCreated');
      this.socket.off('error');
      this.socket.off('playerDied');
      this.socket.off('playerRespawned');
      this.socket.off('membraneExpanded');
      this.socket.off('membraneExpansionFailed');
      this.socket.off('tickUpdate');
      this.socket.off('membraneKeyframe');
      
      // Disconnect the socket
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
      this.lastReceivedTick = 0;
    }
  }

  /**
   * Clean up all callbacks and disconnect
   */
  public destroy(): void {
    // Clear all callbacks
    this.onConnectCallbacks = [];
    this.onDisconnectCallbacks = [];
    this.onRoomStateCallbacks = [];
    this.onFullSnapshotCallbacks = [];
    this.onServerModeCallbacks = [];
    this.onPlayerJoinedCallbacks = [];
    this.onPlayerLeftCallbacks = [];
    this.onPlayerMovedCallbacks = [];
    this.onResourceSpawnedCallbacks = [];
    this.onResourceCollectedCallbacks = [];
    this.onToxinShotCallbacks = [];
    this.onCellDamagedCallbacks = [];
    this.onOrganelleBuiltCallbacks = [];
    this.onRoomsListCallbacks = [];
    this.onRoomCreatedCallbacks = [];
    this.onErrorCallbacks = [];
    this.onPlayerDiedCallbacks = [];
    this.onPlayerRespawnedCallbacks = [];
    this.onMembraneExpandedCallbacks = [];
    this.onMembraneExpansionFailedCallbacks = [];
    this.onTickUpdateCallbacks = [];
    this.onMembraneKeyframeCallbacks = [];
    
    // Disconnect
    this.disconnect();
  }
  
  /**
   * Gracefully leave the current room if in one.
   * This informs other players that we're leaving.
   */
  public leaveCurrentRoom(): void {
    if (!this.socket || !this.connected) {
      console.log('Not connected, no need to leave room');
      return;
    }
    
    // Tell server we're leaving the room
    this.socket.emit('leaveRoom');
    console.log('Sent leaveRoom event to server');
  }

  /**
   * Register a callback for when resources are updated by the server (e.g., due to ATP consumption)
   */
  public onResourcesUpdated(callback: EventCallback<ResourcesUpdatedEvent>): void {
    this.onResourcesUpdatedCallbacks.push(callback);
  }

  public onTickUpdate(callback: EventCallback<TickUpdateEvent>): void {
    this.onTickUpdateCallbacks.push(callback);
  }

  public onMembraneKeyframe(callback: EventCallback<MembraneKeyframeEvent>): void {
    this.onMembraneKeyframeCallbacks.push(callback);
  }

  /**
   * Sends player resource update to the server with full precision
   * @param resources Current resource values
   * @param production Production/consumption rates since last update
   */
  public updatePlayerResources(resources: {
    atp: number;
    glucose: number;
    lipids: number;
  }, production: {
    atpProduced: number;
    glucoseConsumed: number;
    sources: {
      natural: { atp: number };
      mitochondria?: { atp: number; glucose: number; count: number };
      other?: Record<string, { atp?: number; glucose?: number }>;
    }
  }): void {
    // Use exact values without rounding to preserve decimal precision
    if (!this.ensureConnected()) return;
    this.socket!.emit('updatePlayerResources', {
      resources,
      production
    });
  }
} 